import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Title from "../../components/Title.tsx";
import SlideBarGraphs from "./SlideBarGraphs.tsx";
import { getDocumentsStatistics } from "../../services/candidate.service.ts";
import {
  getPendingDocumentsForReview,
  getDocumentReviewStats,
  syncDocumentsForReview,
} from "../../services/proceedings.service.ts";
import {
  getUnsubscribeStats,
  getStaffByPosition,
} from "../../services/partner.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import RoutesGraphs from "./components/RoutesGraphs.tsx";

interface BranchStatistics {
  branch: {
    id: number;
    name: string;
    businessName: string;
  };
  statistics: {
    totalPartners: number;
    partnersWithCredentials: number;
    partnersWithoutCredentials: number;
    partnersWithCompleteDocuments?: number;
    partnersWithIncompleteDocuments?: number;
    totalPendingDocuments: number;
    totalUploadedDocuments?: number;
    averagePendingPerPartner: string;
    documentsByType: { [key: string]: number };
  };
}

const DocumentsGraphs = () => {
  useAppSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(() => {
    const saved = localStorage.getItem("sidebarOpen");
    const isDesktop = typeof window !== "undefined" && window.innerWidth >= 768;
    return saved ? JSON.parse(saved) : isDesktop;
  });

  const [data, setData] = React.useState<BranchStatistics[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);

  const [pendingReviewCount, setPendingReviewCount] = React.useState(0);
  const [reviewedToday, setReviewedToday] = React.useState(0);

  const [unsubscribeStats, setUnsubscribeStats] = React.useState<any>(null);
  const [unsubscribeDays, setUnsubscribeDays] = React.useState(30);
  const [unsubscribeDateRange, setUnsubscribeDateRange] = React.useState<{
    startDate?: string;
    endDate?: string;
  }>({});

  const [staffByPosition, setStaffByPosition] = React.useState<any>(null);

  React.useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && sidebarOpen) {
        setSidebarOpen(false);
      } else if (!mobile && !sidebarOpen) {
        setSidebarOpen(true);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [sidebarOpen]);

  const fetchData = async () => {
    try {
      await syncDocumentsForReview().catch(() => {});

      const [
        statisticsResponse,
        pendingResponse,
        reviewStatsResponse,
        unsubscribeResponse,
        staffByPositionResponse,
      ] = await Promise.all([
        getDocumentsStatistics(),
        getPendingDocumentsForReview({ limit: 1, offset: 0 }),
        getDocumentReviewStats({ requestType: "upload" }),
        getUnsubscribeStats(unsubscribeDays),
        getStaffByPosition(),
      ]);
      setData(statisticsResponse);
      setPendingReviewCount(
        typeof reviewStatsResponse?.pending === "number"
          ? reviewStatsResponse.pending
          : pendingResponse?.total || 0
      );
      setReviewedToday(reviewStatsResponse?.reviewed || 0);
      setUnsubscribeStats(unsubscribeResponse);
      setStaffByPosition(staffByPositionResponse);
    } catch (error) {
      console.error(error);
      notification("Error", "Error al obtener las estadísticas", "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const totalStats = React.useMemo(() => {
    return data.reduce(
      (acc, item) => ({
        totalPartners: acc.totalPartners + item.statistics.totalPartners,
        partnersWithCredentials:
          acc.partnersWithCredentials + item.statistics.partnersWithCredentials,
        partnersWithoutCredentials:
          acc.partnersWithoutCredentials +
          item.statistics.partnersWithoutCredentials,
        totalPendingDocuments:
          acc.totalPendingDocuments + item.statistics.totalPendingDocuments,
      }),
      {
        totalPartners: 0,
        partnersWithCredentials: 0,
        partnersWithoutCredentials: 0,
        totalPendingDocuments: 0,
      }
    );
  }, [data]);

  const barChartData = React.useMemo(() => {
    return data
      .map((item) => ({
        name:
          item.branch.name.length > 15
            ? item.branch.name.substring(0, 15) + "..."
            : item.branch.name,
        fullName: item.branch.name,
        branchId: item.branch.id,
        "Documentos Pendientes": item.statistics.totalPendingDocuments,
        "Documentos Subidos": item.statistics.totalUploadedDocuments || 0,
        Total: item.statistics.totalPendingDocuments,
        "Total Chilitos": item.statistics.totalPartners,
      }))
      .sort((a, b) => b["Documentos Pendientes"] - a["Documentos Pendientes"]);
  }, [data]);

  const pieChartDocumentsData = React.useMemo(() => {
    const documentsByType: { [key: string]: number } = {};
    data.forEach((item) => {
      Object.entries(item.statistics.documentsByType).forEach(
        ([docType, count]) => {
          documentsByType[docType] = (documentsByType[docType] || 0) + count;
        }
      );
    });

    return Object.entries(documentsByType)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .filter((item) => item.value > 0);
  }, [data]);

  const stackedBarData = React.useMemo(() => {
    return data
      .map((item) => ({
        name:
          item.branch.name.length > 15
            ? item.branch.name.substring(0, 15) + "..."
            : item.branch.name,
        "Con Credenciales": item.statistics.partnersWithCredentials,
        "Sin Credenciales": item.statistics.partnersWithoutCredentials,
      }))
      .sort(
        (a, b) =>
          b["Con Credenciales"] +
          b["Sin Credenciales"] -
          (a["Con Credenciales"] + a["Sin Credenciales"])
      );
  }, [data]);

  const branchListData = React.useMemo(() => {
    return data
      .map((item) => ({
        name: item.branch.name,
        branchId: item.branch.id,
        totalPartners: item.statistics.totalPartners,
      }))
      .sort((a, b) => b.totalPartners - a.totalPartners);
  }, [data]);

  const handleViewBranch = (branchId: number) => {
    navigate(`/reportes/documentos/${branchId}`);
  };

  React.useEffect(() => {
    if (location.pathname === "/graficas" && !location.hash) {
      navigate("/graficas#general", { replace: true });
    }
  }, [location.pathname, location.hash, navigate]);

  const handleDateRangeChange = (startDate?: string, endDate?: string) => {
    setUnsubscribeDateRange({ startDate, endDate });
  };

  const renderContent = () => {
    return (
      <RoutesGraphs
        barChartData={barChartData}
        pieChartDocumentsData={pieChartDocumentsData}
        stackedBarData={stackedBarData}
        totalStats={totalStats}
        data={data}
        pendingReviewCount={pendingReviewCount}
        reviewedToday={reviewedToday}
        staffByPosition={staffByPosition}
        unsubscribeStats={unsubscribeStats}
        unsubscribeDays={unsubscribeDays}
        setUnsubscribeDays={setUnsubscribeDays}
        setUnsubscribeStats={setUnsubscribeStats}
        handleViewBranch={handleViewBranch}
        unsubscribeDateRange={unsubscribeDateRange}
        onDateRangeChange={handleDateRangeChange}
        styles={styles}
        loading={loading}
      />
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        minHeight: "100vh",
        position: "relative",
      }}
    >
      <SlideBarGraphs
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          marginLeft: sidebarOpen && !isMobile ? "0" : "0",
          transition: "margin-left 0.3s ease-in-out",
          width:
            sidebarOpen && !isMobile ? "calc(100% - 280px - 28px)" : "100%",
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <div
          style={{
            ...styles.container,
            paddingLeft: isMobile ? "12px" : "20px",
            paddingRight: isMobile ? "12px" : "20px",
            flex: 1,
            maxWidth: "100%",
          }}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "16px",
    paddingTop: "24px",
    minHeight: "calc(100vh - 120px)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    position: "relative",
    overflowX: "hidden",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
    width: "100%",
    alignItems: "stretch",
    gridAutoRows: "1fr",
  },
  statCard: {
    borderRadius: "12px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",
    minWidth: 0,
    height: "100%",
    boxSizing: "border-box",
  },
  statIconWrapper: {
    width: "56px",
    height: "56px",
    minWidth: "56px",
    minHeight: "56px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statIcon: {
    fontSize: "24px",
    color: "#fff",
  },
  statContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "4px",
    minWidth: 0,
    height: "100%",
  },
  statValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#fff",
    lineHeight: 1.2,
    margin: 0,
  },
  statLabel: {
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.9)",
    margin: 0,
    lineHeight: 1.2,
  },
  statCardBlue: {
    background: "linear-gradient(135deg, #800080 0%, #4B0082 100%)",
  },
  statCardRed: {
    background: "linear-gradient(135deg, #FF69B4 0%, #FA0A0A 100%)",
  },
  statCardOrange: {
    background: "linear-gradient(135deg, #FFA500 0%, #F7B917 100%)",
  },
  statCardGreen: {
    background: "linear-gradient(135deg, #50B7C0 0%, #008080 100%)",
  },
  statCardPurple: {
    background: "linear-gradient(135deg, #FF69B4 0%, #800080 100%)",
  },
  twoColumnRow: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap" as const,
    width: "100%",
  },
  chartCard: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    width: "100%",
  },
  chartHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
  chartHeaderContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
  },
  chartTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  exportChartButton: {
    background: "#22c55e",
    border: "1px solid #22c55e",
    borderRadius: "8px",
    padding: "8px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    minWidth: "auto",
  },
  exportChartIcon: {
    color: "#fff",
    fontSize: "14px",
    transition: "color 0.2s ease",
  },
  exportChartText: {
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    margin: 0,
  },
  chartTitleIcon: {
    fontSize: "20px",
    color: "#667eea",
  },
  chartBody: {
    padding: "28px",
  },
  customTooltip: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  tooltipLabel: {
    fontWeight: "600",
    marginBottom: "8px",
    color: "#2d3748",
  },
  branchInfoSection: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "2px solid #e5e7eb",
  },
  branchInfoTitle: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#2d3748",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  branchInfoIcon: {
    fontSize: "18px",
    color: "#2563eb",
  },
  branchTableContainer: {
    maxHeight: "280px",
    overflowY: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    backgroundColor: "#fff",
  },
  branchTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  branchTableHeaderRow: {
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  branchTableHeader: {
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "600",
    color: "#374151",
    fontSize: "13px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  branchTableRow: {
    borderBottom: "1px solid #e5e7eb",
    transition: "background-color 0.2s ease",
    cursor: "default",
  },
  branchTableRowHover: {
    backgroundColor: "#f9fafb",
  },
  branchTableCell: {
    padding: "14px 16px",
    color: "#374151",
  },
  branchTableCellContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  branchTableEyeButton: {
    background: "transparent",
    border: "1px solid #2563eb",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  branchTableEyeIcon: {
    fontSize: "14px",
    color: "#2563eb",
    transition: "color 0.2s ease",
  },
  reviewGoButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    borderRadius: "12px",
    color: "#fff",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
  },
  unsubscribeStatCard: {
    borderRadius: "12px",
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: 1,
  },
  unsubscribeStatIcon: {
    width: "48px",
    height: "48px",
    background: "rgba(255, 255, 255, 0.25)",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "20px",
    color: "#fff",
  },
  unsubscribeStatValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#fff",
    lineHeight: 1,
  },
  unsubscribeStatLabel: {
    fontSize: "13px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "4px",
  },
  unsubscribeStatCardSmall: {
    borderRadius: "10px",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  unsubscribeStatIconSmall: {
    width: "36px",
    height: "36px",
    background: "rgba(255, 255, 255, 0.25)",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    color: "#fff",
  },
  unsubscribeStatValueSmall: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#fff",
    lineHeight: 1,
  },
  unsubscribeStatLabelSmall: {
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "2px",
  },
};

export default DocumentsGraphs;
